import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function BlogArticle() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.get(`/blog/${slug}`)
            .then(({ data }) => {
                setArticle(data);
                const title = `${data.meta_title || data.title} | Japet Agencement`;
                document.title = title;

                const upsertMeta = (selector, attrs) => {
                    let el = document.querySelector(selector);
                    if (!el) {
                        el = document.createElement("meta");
                        Object.entries(attrs).forEach(([k, v]) => { if (k !== "content") el.setAttribute(k, v); });
                        document.head.appendChild(el);
                    }
                    el.setAttribute("content", attrs.content);
                };

                const desc = data.meta_description || data.excerpt || "";
                upsertMeta('meta[name="description"]', { name: "description", content: desc });
                upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
                upsertMeta('meta[property="og:description"]', { property: "og:description", content: desc });
                upsertMeta('meta[property="og:type"]', { property: "og:type", content: "article" });
                upsertMeta('meta[property="og:url"]', { property: "og:url", content: window.location.href });
                if (data.cover_image) {
                    upsertMeta('meta[property="og:image"]', { property: "og:image", content: data.cover_image });
                    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: data.cover_image });
                }
                upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
                upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
                upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: desc });

                // Canonical
                let canonical = document.querySelector('link[rel="canonical"]');
                if (!canonical) {
                    canonical = document.createElement("link");
                    canonical.rel = "canonical";
                    document.head.appendChild(canonical);
                }
                canonical.href = window.location.href;

                // JSON-LD Article
                const existing = document.getElementById("ld-article");
                if (existing) existing.remove();
                const ld = document.createElement("script");
                ld.type = "application/ld+json";
                ld.id = "ld-article";
                ld.innerText = JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Article",
                    "headline": data.title,
                    "description": desc,
                    "image": data.cover_image ? [data.cover_image] : undefined,
                    "datePublished": data.created_at,
                    "dateModified": data.updated_at,
                    "author": { "@type": "Organization", "name": "Japet Agencement" },
                    "publisher": {
                        "@type": "Organization",
                        "name": "Japet Agencement",
                        "logo": { "@type": "ImageObject", "url": `${window.location.origin}/japet-logo.png` },
                    },
                    "mainEntityOfPage": window.location.href,
                    "keywords": (data.tags || []).join(", "),
                });
                document.head.appendChild(ld);
            })
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) {
        return <div className="max-w-3xl mx-auto px-6 py-32 text-brand-sageDark" data-testid="article-loading">Chargement…</div>;
    }
    if (error || !article) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32" data-testid="article-error">
                <h1 className="font-serif text-4xl text-brand-ink">Article introuvable</h1>
                <p className="mt-3 text-brand-sageDark">{error}</p>
                <Link to="/blog" className="mt-6 inline-block text-brand-sage underline">← Retour au journal</Link>
            </div>
        );
    }

    return (
        <article className="max-w-3xl mx-auto px-6 md:px-12 py-20" data-testid="article-page">
            <Link to="/blog" data-testid="article-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-sage hover:text-brand-sageDark">
                <ArrowLeft size={14} /> Retour au blog
            </Link>
            <header className="mt-10">
                {article.tags?.length > 0 && (
                    <span className="overline">{article.tags[0]}</span>
                )}
                <h1 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.1] text-brand-ink">
                    {article.title}
                </h1>
                <p className="mt-6 text-lg text-brand-sageDark italic font-light">{article.excerpt}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.22em] text-brand-sage">
                    {new Date(article.created_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}
                </p>
            </header>
            {article.cover_image && (
                <img src={article.cover_image} alt={article.title} className="mt-10 w-full aspect-[16/9] object-cover" />
            )}
            <div className="prose-japet mt-12" dangerouslySetInnerHTML={{ __html: article.content }} />
        </article>
    );
}
